/**
 * ShareDialog Component
 *
 * Sheet-based dialog for managing document or session sharing permissions.
 * Allows owners to grant/revoke access with role-based permissions.
 *
 * Document mode targets the generic resource-share surface
 * (POST/GET/DELETE /documents/{id}/shares): grants go to people (user),
 * role groups (group) or org units (node). Mutations are SERVER-
 * AUTHORITATIVE (clinical-safety carve-out): call the API, then re-read
 * committed state — no optimistic edits. A group/node share in an
 * approval-required environment returns 202 APPROVAL_PENDING, surfaced as
 * an INFO state naming the reviewers, not an error.
 *
 * Session mode is unchanged (per-user permissions via the session endpoints).
 */

import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Share2, Trash2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { createLogger } from '@/utils/logger';
import { sanitizeErrorMessage } from '@/utils/errorMessages';

// The sharing contract, one client for every kind. The backend serves
// documents, sessions and units through the same handlers, so the client is
// one function per verb keyed on the kind rather than a trio per kind.
import {
  listResourceShares,
  shareResourceOfKind,
  revokeResourceShareOfKind,
  type ResourceGrant,
  type ShareableKind,
  type SharePrincipalType,
  type ShareRole,
} from '@/api/endpoints/sharing';
import { isApprovalPending } from '@/api/endpoints/accessRequests';
import {
  publishDocument,
  type Document,
  type PublishScope,
} from '@/api/endpoints/documents';
import { PendingPublishChips } from '@/components/documents/VisibilityChips';
import { useMyAccessRequests } from '@/hooks/useAccess';
import { DOCUMENTS_QUERY_KEY } from '@/lib/queryKeys';

// Role/unit pickers — same fetchers + query keys as the org console, so the
// catalogue is shared with (and cached alongside) the admin views.
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { useConfigStore } from '@/stores/config';
import { QUERY_STALE_TIMES } from '@/lib/query-config';
import { orgKeys } from '@/components/admin/org/useOrg';
import { orgApi, type Location, type Role } from '@/api/endpoints/org';

// `AccessLevel` is the role union the UnifiedShare rows carry.
import type { ShareRole as AccessLevel } from '@/api/endpoints/sharing';

import { searchUsers } from '@/api/endpoints/users';
import {
  UserSearchCombobox,
  userDisplayName,
  type SearchDataSource,
} from '@/components/user/UserSearchCombobox';

const logger = createLogger('ShareDialog');

/** People picker source: the live directory typeahead. Debounce, sequence
 *  guard and the "showing X of Y" hint all live in the combobox. Module-level
 *  so its identity is stable across renders. */
const DIRECTORY_SEARCH: SearchDataSource = { type: 'server', searchFn: searchUsers };

/**
 * What a shareable kind declares about its own sharing UI.
 *
 * The backend serves every kind through the same handlers, so what actually
 * differs is copy and which principals the ACL model accepts — both of which
 * are data. This table holds them, and the dialog reads it. Before, each
 * difference was an `isDocument ?` at the point of use, which is why a unit
 * could not be shared through this dialog at all despite the API supporting it
 * since the generic surface landed: `node` had nowhere to be declared.
 *
 * Adding a kind is a row here.
 */
interface ShareableKindUI {
  /** i18n namespace for this kind's sharing copy. */
  i18nPrefix: string;
  /**
   * Principal kinds the MODEL accepts on this resource. A session has no
   * node#viewer form, so a unit-to-session share is model-rejected as 400 —
   * offering the tab would invite an error the user cannot act on.
   */
  principalKinds: SharePrincipalType[];
  /**
   * Offer a self-expiring share. Only where the model has a conditioned form
   * for the tier; elsewhere the server answers 422 rather than dropping the
   * expiry silently, and a control that always errors is worse than none.
   */
  supportsExpiry: boolean;
  /**
   * Label every row with its principal kind, not just non-user rows. Useful
   * where people, roles and units are mixed; noise where every row is a person.
   */
  labelEveryPrincipalKind: boolean;
  addHeading: { key: string; fallback: string };
  accessHeading: { key: string; fallback: string };
  title: string;
  info: string;
}

const SHAREABLE_KINDS: Record<ShareableKind, ShareableKindUI> = {
  document: {
    i18nPrefix: 'documents',
    principalKinds: ['user', 'group', 'node'],
    supportsExpiry: false,
    labelEveryPrincipalKind: true,
    addHeading: { key: 'documents.sharing.addTargets', fallback: 'Share with' },
    accessHeading: { key: 'documents.sharing.withAccess', fallback: 'Who has access' },
    title: 'Share Document',
    info: 'Only people with access can find this document in search',
  },
  session: {
    i18nPrefix: 'sessions',
    principalKinds: ['user', 'group'],
    supportsExpiry: true,
    labelEveryPrincipalKind: false,
    addHeading: { key: 'sessions.sharing.addPeople', fallback: 'Add people' },
    accessHeading: { key: 'sessions.sharing.peopleWithAccess', fallback: 'People with access' },
    title: 'Share Session',
    info: 'Only people with access can view this session',
  },
  node: {
    i18nPrefix: 'locations',
    principalKinds: ['user', 'group'],
    supportsExpiry: true,
    labelEveryPrincipalKind: false,
    addHeading: { key: 'locations.sharing.addPeople', fallback: 'Give access to' },
    accessHeading: { key: 'locations.sharing.peopleWithAccess', fallback: 'Who works here' },
    title: 'Share Unit',
    info: 'Only people with access can see this unit and its patients',
  },
};

interface ShareDialogProps {
  resourceId: string;
  resourceTitle: string;
  /**
   * Which shareable kind this is. An open union rather than the old
   * `'document' | 'session'`: the dialog reads its behaviour from
   * SHAREABLE_KINDS below, so a kind is a row in that table and not a branch
   * in here — which is why `node` (a unit) now works without touching this
   * component.
   */
  resourceType: ShareableKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The document row (document mode only) — drives the Visibility section
   * (Private ↔ Organisation select + pending-approval chips). The dialog is
   * the ONE place a document's audience is managed: org-wide visibility,
   * role/unit shares, and per-person grants together. Server-authoritative:
   * a change invalidates the documents list and the select reflects the
   * refetched committed state, never an optimistic flip.
   */
  doc?: Document;
}

// Internal unified share type for rendering
interface UnifiedShare {
  id: string;
  principalType: SharePrincipalType;
  principalId: string;
  email: string;
  displayName: string;
  role: ShareRole | AccessLevel;
  grantedBy: string;
  grantedAt: string;
}

export default function ShareDialog({
  resourceId,
  resourceTitle,
  resourceType,
  open,
  onOpenChange,
  doc,
}: ShareDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const kind = SHAREABLE_KINDS[resourceType];
  const supportsNodes = kind.principalKinds.includes('node');
  // Tabs are the kind's accepted principals, in the model's own order — never
  // a tab the ACL would reject.
  const principalTabLabel: Record<SharePrincipalType, string> = {
    user: t('documents.sharing.target.people', 'People'),
    group: t('documents.sharing.target.roles', 'Roles'),
    node: t('documents.sharing.target.units', 'Units'),
  };
  const targetKinds: [SharePrincipalType, string][] = kind.principalKinds.map((k) => [
    k,
    principalTabLabel[k],
  ]);

  const [shares, setShares] = useState<UnifiedShare[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // INFO state (not an error): a share was turned into a pending publish
  // request — names the reviewers so the owner knows who decides.
  const [info, setInfo] = useState<string | null>(null);

  // Add section state
  const [targetKind, setTargetKind] = useState<SharePrincipalType>('user');
  const [newEmail, setNewEmail] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [newRole, setNewRole] = useState<ShareRole | AccessLevel>('viewer');
  // A single in-flight flag for both modes (both are now server-authoritative
  // POST-then-refetch — no optimistic edits on an ACL mutation).
  const [isSharingState, setIsSharingState] = useState(false);
  const isSharing = isSharingState;
  // Session share expiry (viewer-tier only, per the wire): a `datetime-local`
  // custom cutoff or none. NO shift-boundary quick-pick — GET /config exposes no
  // single "end of this shift" instant, so the layer never fabricates one.
  const [sessionExpiresAt, setSessionExpiresAt] = useState('');

  // Role/unit catalogues for the document pickers. Config-gated like the org
  // console hooks (orgPageSize() requires the /config-served page size) and
  // fetched only while the matching picker is actually on screen.
  const isConfigLoaded = useConfigStore((s) => s.isConfigLoaded);
  const rolesQuery = useAuthenticatedQuery<Role[]>({
    queryKey: orgKeys.roles(),
    queryFn: () => orgApi.listAllRoles(),
    // Both documents and sessions offer the Roles tab now.
    enabled: open && targetKind === 'group' && isConfigLoaded,
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  });
  const locationsQuery = useAuthenticatedQuery<Location[]>({
    queryKey: orgKeys.locations(),
    queryFn: () => orgApi.listAllLocations(),
    enabled: open && supportsNodes && targetKind === 'node' && isConfigLoaded,
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  });

  // User search typeahead — the id is set only by picking a suggestion; typing
  // clears it, so a free-typed address shares by email instead.
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const handleEmailChange = useCallback((value: string) => {
    setNewEmail(value);
    setSelectedUserId(null);
  }, []);

  // Removal state
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Role-change in flight (disables that row's role select, same pattern as
  // removingId) — without it a second select change fires a concurrent
  // updateShare against the same grant.
  const [roleChangingId, setRoleChangingId] = useState<string | null>(null);

  const fetchShares = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await listResourceShares(resourceType, resourceId);
      const newShares: UnifiedShare[] = response.map((grant: ResourceGrant) => ({
        id: `${grant.principal_type}:${grant.principal_id}`,
        principalType: grant.principal_type,
        principalId: grant.principal_id,
        email: grant.principal_email ?? '',
        // Server-resolved display name (user name/email, role name, unit
        // name); fall back to email, then raw id, so a stale grant whose
        // resolution missed still renders identifiably.
        displayName: grant.principal_name || grant.principal_email || grant.principal_id,
        role: grant.access_level,
        grantedBy: grant.granted_by,
        grantedAt: grant.granted_at,
      }));
      setShares(newShares);
      logger.info('Shares loaded', { resourceId, resourceType, count: newShares.length });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load shares';
      logger.error('Failed to fetch shares', { resourceId, resourceType, error: msg });
      setError(sanitizeErrorMessage(msg));
    } finally {
      setIsLoading(false);
    }
  }, [resourceId, resourceType]);

  // Fetch shares when dialog opens
  useEffect(() => {
    if (open) {
      void fetchShares();
    }
  }, [open, fetchShares]);

  const kindLabel = (kind: SharePrincipalType): string => {
    switch (kind) {
      case 'group':
        return t('documents.sharing.kind.role', 'Role');
      case 'node':
        return t('documents.sharing.kind.unit', 'Unit');
      default:
        return t('documents.sharing.kind.person', 'Person');
    }
  };

  const approvalInfoMessage = (approverNames: string[]): string =>
    t('documents.sharing.sentForApproval', {
      names: approverNames.join(', '),
      defaultValue: 'Sent for approval — will be reviewed by {{names}}',
    });

  // Org-wide publish, server-authoritative: apply, then invalidate the
  // documents list — the select reflects the refetched committed state (or the
  // pending chip, when an approval-required environment turned the expansion
  // into a pending access request).
  const [isChangingVisibility, setIsChangingVisibility] = useState(false);
  // The document's open publish requests are the caller's own access
  // requests on it: one read of GET /access-requests, filtered here. The
  // document row does not carry them (tricorder-knowledge SPEC.md 2.8).
  const myRequests = useMyAccessRequests();
  const pendingPublishes = (myRequests.data ?? []).filter(
    (r) => doc != null && r.resource_type === 'document' && r.resource_id === doc.id && r.status === 'pending',
  );
  const handlePublish = async (scope: PublishScope) => {
    if (!doc) return;
    if ((scope === 'public') === (doc.visibility?.org_wide ?? false)) return;
    setError(null);
    setInfo(null);
    setIsChangingVisibility(true);
    try {
      const result = await publishDocument(doc.id, scope);
      if (result.status === 'pending') {
        setInfo(approvalInfoMessage(result.approvers.map((a) => a.display_name)));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Publish failed';
      logger.error('Publish failed', { resourceId, scope, error: msg });
      setError(t('documents.list.visibility.changeError', 'Could not change visibility. Please try again.'));
    } finally {
      setIsChangingVisibility(false);
      // Invalidate on every outcome (same shape as the approvals inbox):
      // repairs the select when the change landed server-side but the
      // response was lost.
      await queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY });
      await myRequests.refetch();
    }
  };

  const switchTargetKind = (kind: SharePrincipalType) => {
    setTargetKind(kind);
    setSelectedGroupId('');
    setError(null);
    setInfo(null);
  };

  const handleShare = async () => {
    if (targetKind !== 'user') {
      if (!selectedGroupId) {
        setError(
          targetKind === 'group'
            ? t('documents.sharing.error.roleRequired', 'Select a role')
            : t('documents.sharing.error.unitRequired', 'Select a unit')
        );
        return;
      }
    } else if (!newEmail.trim()) {
      setError(
        t(
          `${kind.i18nPrefix}.sharing.error.emailRequired`,
          'Email is required'
        )
      );
      return;
    }

    setError(null);
    setInfo(null);

    // 202 APPROVAL_PENDING: the share became a pending publish request —
    // nothing changed server-side, so re-reading the list would show nothing
    // new. Skip the refetch and tell the owner when the share will appear.
    let pendingApproval = false;

    try {
      setIsSharingState(true);
      try {
        const principal =
          targetKind === 'user'
            ? selectedUserId
              ? { type: 'user' as const, id: selectedUserId }
              : { type: 'user' as const, email: newEmail.trim() }
            : { type: targetKind, id: selectedGroupId };
        // expires_at is offered only where the kind declares it, so it is
        // undefined elsewhere and the request is byte-identical to before.
        const result = await shareResourceOfKind(resourceType, resourceId, {
          principal,
          access_level: newRole as ShareRole,
          expires_at: sessionExpiresAt ? new Date(sessionExpiresAt).toISOString() : undefined,
        });
        // 202: an audience-widening share on an approval-gated resource became
        // a pending access request instead of a grant. Only documents are
        // approval-gated today, but the check is on the RESPONSE rather than
        // the kind — if another kind becomes gated, this keeps telling the
        // truth instead of silently reporting a grant that was not written.
        if (isApprovalPending(result)) {
          pendingApproval = true;
          setInfo(
            t('documents.sharing.sentForApprovalWillAppear', {
              names: result.approvers.map((a) => a.display_name).join(', '),
              defaultValue:
                'Sent for approval — the share will appear in the list once reviewed by {{names}}',
            })
          );
        }
      } finally {
        setIsSharingState(false);
      }
      logger.info('Share added', { resourceId, resourceType, targetKind, role: newRole });

      // Reset form
      setNewEmail('');
      setSelectedGroupId('');
      setNewRole('viewer');
      setSelectedUserId(null);
      setSessionExpiresAt('');

      // Refresh shares list (server-authoritative: committed state only).
      // Skipped when the share went to approval — the grant set is unchanged.
      if (!pendingApproval) {
        await fetchShares();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add share';
      logger.error('Failed to add share', { resourceId, resourceType, error: msg });
      setError(sanitizeErrorMessage(msg));
    }
  };

  const handleRoleChange = async (share: UnifiedShare, newRole: ShareRole | AccessLevel) => {
    setRoleChangingId(share.id);
    setError(null);
    setInfo(null);

    try {
      // POST again with the new level — the ACL grant is an idempotent upsert,
      // for every kind. A group or node change can come back APPROVAL_PENDING
      // in an approval-required environment, same as a fresh share.
      const result = await shareResourceOfKind(resourceType, resourceId, {
        principal: { type: share.principalType, id: share.principalId },
        access_level: newRole as ShareRole,
      });
      logger.info('Share role updated', {
        resourceId,
        resourceType,
        shareId: share.id,
        oldRole: share.role,
        newRole,
      });
      if (isApprovalPending(result)) {
        // 202: the level change became a pending access request — nothing
        // changed server-side, so skip the re-read (the select snaps back to
        // the committed level on re-render).
        setInfo(approvalInfoMessage(result.approvers.map((a) => a.display_name)));
      } else {
        // Server-authoritative: re-read committed state, no local flip.
        await fetchShares();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update share';
      logger.error('Failed to update share', { resourceId, resourceType, error: msg });
      setError(sanitizeErrorMessage(msg));
    } finally {
      setRoleChangingId(null);
    }
  };

  const handleRemove = async (share: UnifiedShare) => {
    setRemovingId(share.id);
    setError(null);
    setInfo(null);

    try {
      // Server-authoritative for every kind: the row disappears only after the
      // server confirms the revoke (clinical-safety carve-out — an optimistic
      // removal would show access as gone while it still holds).
      await revokeResourceShareOfKind(
        resourceType,
        resourceId,
        share.principalType,
        share.principalId
      );
      logger.info('Share removed', { resourceId, resourceType, shareId: share.id });
      await fetchShares();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove share';
      logger.error('Failed to remove share', { resourceId, resourceType, error: msg });
      // Translate gRPC errors to user-friendly messages
      if (msg.includes('last owner') || msg.includes('FailedPrecondition')) {
        setError(t('sharing.error.lastOwner', 'Cannot remove the last owner. Transfer ownership first.'));
      } else {
        setError(sanitizeErrorMessage(msg));
      }
    } finally {
      setRemovingId(null);
    }
  };

  // Copy keys are the kind's namespace plus the same leaf everywhere, so a new
  // kind needs translations rather than a branch in here.
  const titleKey = `${kind.i18nPrefix}.sharing.title`;
  const emailPlaceholderKey = `${kind.i18nPrefix}.sharing.emailPlaceholder`;
  const noSharesKey = `${kind.i18nPrefix}.sharing.noShares`;
  const infoKey = `${kind.i18nPrefix}.sharing.info`;

  // The picker rendered for the active non-user target kind. People = user
  // search (rendered elsewhere); Roles (both modes) / Units (documents) =
  // catalogue selects with explicit loading / error / empty states.
  const renderTargetPicker = () => {
    if (targetKind === 'user') return null;
    const query = targetKind === 'group' ? rolesQuery : locationsQuery;
    if (query.isLoading || (!query.data && !query.isError)) {
      return (
        <div className="flex-1 flex items-center px-3 text-sm text-neutral-500 min-h-[44px]">
          {t('common.loading', 'Loading...')}
        </div>
      );
    }
    if (query.isError) {
      return (
        <div className="flex-1 flex items-center justify-between gap-2 px-3 text-sm text-red-700 min-h-[44px]">
          <span>
            {targetKind === 'group'
              ? t('documents.sharing.rolesLoadError', 'Could not load roles')
              : t('documents.sharing.unitsLoadError', 'Could not load units')}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void query.refetch()}
            className="min-h-[44px]"
          >
            {t('actions.retry', 'Retry')}
          </Button>
        </div>
      );
    }
    const options: { id: string; name: string }[] = query.data ?? [];
    if (options.length === 0) {
      return (
        <div className="flex-1 flex items-center px-3 text-sm text-neutral-500 min-h-[44px]">
          {targetKind === 'group'
            ? t('documents.sharing.noRoles', 'No roles available')
            : t('documents.sharing.noUnits', 'No units available')}
        </div>
      );
    }
    return (
      <select
        value={selectedGroupId}
        onChange={(e) => setSelectedGroupId(e.target.value)}
        disabled={isSharing}
        aria-label={
          targetKind === 'group'
            ? t('documents.sharing.kind.role', 'Role')
            : t('documents.sharing.kind.unit', 'Unit')
        }
        className="flex-1 h-10 min-h-[44px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <option value="">
          {targetKind === 'group'
            ? t('documents.sharing.selectRole', 'Select a role')
            : t('documents.sharing.selectUnit', 'Select a unit')}
        </option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            {t(titleKey, kind.title)}
          </SheetTitle>
          <SheetDescription>{resourceTitle}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Visibility (documents only): the org-wide audience switch, with
              open expansion requests as pending chips. Role/unit/person
              grants are managed in the sections below. */}
          {doc && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-neutral-900">
                {t('documents.sharing.visibility', 'Visibility')}
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={doc.visibility?.org_wide ? 'public' : 'private'}
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next !== 'private' && next !== 'public') return;
                    void handlePublish(next);
                  }}
                  disabled={isChangingVisibility}
                  aria-label={t('documents.sharing.visibility', 'Visibility')}
                  className="h-10 min-h-[44px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="private">{t('documents.list.visibility.private', 'Private')}</option>
                  <option value="public">{t('documents.list.visibility.organisation', 'Organisation')}</option>
                </select>
                <PendingPublishChips pending={pendingPublishes} />
                {myRequests.isError && (
                  <span role="alert" className="text-xs text-red-700">
                    {t('documents.sharing.pendingError', 'Could not load pending approvals.')}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Add section */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-neutral-900">
              {t(kind.addHeading.key, kind.addHeading.fallback)}
            </h3>

            {/* Target-kind selector: People / Roles (both) + Units (documents). */}
            {targetKinds.length > 1 && (
              <div className="flex gap-1 rounded-lg bg-neutral-100 p-1" role="tablist">
                {targetKinds.map(([kind, label]) => (
                  <button
                    key={kind}
                    type="button"
                    role="tab"
                    aria-selected={targetKind === kind}
                    onClick={() => switchTargetKind(kind)}
                    disabled={isSharing}
                    className={`flex-1 min-h-[44px] rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      targetKind === kind
                        ? 'bg-white text-neutral-900 shadow-sm'
                        : 'text-neutral-600 hover:text-neutral-900'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); void handleShare(); }} className="space-y-3">
              <div className="flex gap-2">
              {targetKind !== 'user' ? (
                renderTargetPicker()
              ) : (
              <div className="flex-1">
                <UserSearchCombobox
                  value={newEmail}
                  onChange={handleEmailChange}
                  onSelect={(user) => {
                    setNewEmail(userDisplayName(user));
                    setSelectedUserId(user.id);
                  }}
                  dataSource={DIRECTORY_SEARCH}
                  selectedUserId={selectedUserId}
                  placeholder={t(emailPlaceholderKey, 'Name or email')}
                  disabled={isSharing}
                />
              </div>
              )}
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as ShareRole | AccessLevel)}
                disabled={isSharing}
                aria-label={t('sharing.roleLabel', 'Access level')}
                className="h-10 min-h-[44px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="viewer">
                  {t(
                    `${resourceType}s.sharing.role.viewer`,
                    'Viewer'
                  )}
                </option>
                <option value="editor">
                  {t(
                    `${resourceType}s.sharing.role.editor`,
                    'Editor'
                  )}
                </option>
              </select>
            </div>
            {/* Session share expiry (viewer-tier). No shift-boundary quick-pick:
                GET /config has no single "end of this shift" instant, so we offer
                a custom cutoff or none — never a fabricated shift time. */}
            {kind.supportsExpiry && (
              <div className="space-y-1.5">
                <label
                  htmlFor="session-share-expiry"
                  className="block text-sm font-medium text-neutral-700"
                >
                  {t('sessions.sharing.expiry', 'Expires (optional)')}
                </label>
                <input
                  id="session-share-expiry"
                  type="datetime-local"
                  value={sessionExpiresAt}
                  onChange={(e) => setSessionExpiresAt(e.target.value)}
                  disabled={isSharing}
                  className="h-10 w-full min-h-[44px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="text-xs text-neutral-500">
                  {t(
                    'sessions.sharing.expiryHint',
                    'Time-boxed shares apply to viewer access only.',
                  )}
                </p>
              </div>
            )}
            <Button
              type="button"
              onClick={handleShare}
              disabled={
                isSharing ||
                (targetKind !== 'user' ? !selectedGroupId : !newEmail.trim())
              }
              className="w-full min-h-[44px]"
            >
              {isSharing
                ? t(
                    `${resourceType}s.sharing.sharing`,
                    'Sharing...'
                  )
                : t(
                    `${resourceType}s.sharing.share`,
                    'Share'
                  )}
            </Button>
          </form>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Pending-approval info (not an error): the share is on its way to
              the named reviewers; nothing changes until they decide. */}
          {info && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-sm text-primary-700">
              {info}
            </div>
          )}

          {/* Current shares list */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-neutral-900">
              {t(kind.accessHeading.key, kind.accessHeading.fallback)}
            </h3>

            {isLoading ? (
              <div className="py-8 text-center text-sm text-neutral-500">
                {t('common.loading', 'Loading...')}
              </div>
            ) : shares.length === 0 ? (
              <div className="py-8 text-center text-sm text-neutral-500">
                {t(noSharesKey, 'No one else has access yet')}
              </div>
            ) : (
              <div className="space-y-2">
                {shares.map((share) => {
                  const isRemoving = removingId === share.id;
                  const isOwner = share.role === 'owner';
                  const ownerCount = shares.filter((s) => s.role === 'owner').length;
                  const isLastOwner = isOwner && ownerCount <= 1;

                  return (
                    <div
                      key={share.id}
                      className="flex items-center justify-between gap-3 p-3 border border-neutral-200 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="text-sm font-medium text-neutral-900 truncate">
                            {share.displayName}
                          </p>
                          {(kind.labelEveryPrincipalKind || share.principalType !== 'user') && (
                            <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-neutral-100 text-neutral-600">
                              {kindLabel(share.principalType)}
                            </span>
                          )}
                        </div>
                        {share.email && share.email !== share.displayName && (
                          <p className="text-xs text-neutral-500 truncate">{share.email}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {isOwner ? (
                          <span className="h-9 min-h-[44px] px-2 py-1 text-xs text-neutral-500 flex items-center">
                            {t(`${resourceType}s.sharing.role.owner`, 'Owner')}
                          </span>
                        ) : (
                          <select
                            value={share.role}
                            onChange={(e) =>
                              handleRoleChange(
                                share,
                                e.target.value as ShareRole | AccessLevel
                              )
                            }
                            disabled={isRemoving || roleChangingId === share.id}
                            aria-label={t('sharing.roleLabel', 'Access level')}
                            className="h-9 min-h-[44px] rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <option value="viewer">
                              {t(`${resourceType}s.sharing.role.viewer`, 'Viewer')}
                            </option>
                            <option value="editor">
                              {t(`${resourceType}s.sharing.role.editor`, 'Editor')}
                            </option>
                          </select>
                        )}
                        {isLastOwner ? (
                          <div className="h-9 w-9 min-h-[44px] min-w-[44px]" />
                        ) : (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRemove(share)}
                            disabled={isRemoving}
                            className="h-9 w-9 min-h-[44px] min-w-[44px] text-red-600 hover:bg-red-50"
                            aria-label={t(
                              `${resourceType}s.sharing.remove`,
                              'Remove access'
                            )}
                          >
                            {isRemoving ? (
                              <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info text */}
          <div className="pt-4 border-t border-neutral-200">
            <p className="text-xs text-neutral-500">
              {t(infoKey, kind.info)}
            </p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
